<template>
  <div class="chart-box">
    <svg :viewBox="`0 0 ${W} ${H}`" class="chart" @mouseleave="hover = null">
      <defs>
        <pattern :id="hatchId" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <line x1="0" y1="0" x2="0" y2="6" class="hatch-line" />
        </pattern>
      </defs>
      <g v-for="r in rows" :key="r.year" @mouseenter="hover = r">
        <rect :x="x(r.year) - slot / 2" :y="PAD.t" :width="slot" :height="H - PAD.t - PAD.b" fill="transparent" />
        <rect
          :x="x(r.year) - barW / 2"
          :y="r.operatingResult >= 0 ? y(r.operatingResult) : y(0)"
          :width="barW"
          :height="Math.max(1, Math.abs(y(r.operatingResult) - y(0)))"
          :fill="fillFor(r)"
          :opacity="hover && hover !== r ? 0.6 : 1"
        />
        <text
          v-if="r.year <= 2027"
          :x="x(r.year)" :y="y(Math.max(r.operatingResult, 0)) - 7"
          class="value" :class="{ 'value-red': r.year === 2027 }" text-anchor="middle"
        >{{ fmtM(r.operatingResult) }}</text>
      </g>
      <line :x1="PAD.l" :x2="W - PAD.r" :y1="y(0)" :y2="y(0)" class="zero" />
      <text v-for="r in rows" :key="`x${r.year}`" :x="x(r.year)" :y="H - PAD.b + 16" class="tick" :class="{ 'tick-strong': r.year === 2027 }" text-anchor="middle">
        {{ r.year }}
      </text>
    </svg>
    <div v-if="hover" class="tooltip" :style="tooltipStyle">
      <span class="tt-title">{{ hover.year }}</span>
      operating result {{ fmtM(hover.operatingResult) }}
      ({{ ((hover.operatingResult / hover.totalIncome) * 100).toFixed(1) }}% of income)
    </div>
  </div>
</template>

<script>
import { scaleLinear } from '../../utils/chartUtils';
import { formatM } from '../../utils/boardEngine';

let uid = 0;

export default {
  name: 'SurplusBars',
  props: { rows: { type: Array, required: true } },
  data: () => ({ W: 1000, H: 190, PAD: { l: 24, r: 24, t: 26, b: 28 }, hover: null, hatchId: `hatch-sb-${uid++}` }),
  computed: {
    x() {
      const years = this.rows.map((r) => r.year);
      return scaleLinear([Math.min(...years), Math.max(...years)], [this.PAD.l + 40, this.W - this.PAD.r - 40]);
    },
    slot() { return (this.W - this.PAD.l - this.PAD.r) / this.rows.length; },
    barW() { return Math.min(38, this.slot - 14); },
    yDomain() {
      const vals = [...this.rows.map((r) => r.operatingResult), 0];
      const min = Math.min(...vals), max = Math.max(...vals);
      const pad = (max - min || 1) * 0.2;
      return [min < 0 ? min - pad : 0, max + pad];
    },
    y() { return scaleLinear(this.yDomain, [this.H - this.PAD.b, this.PAD.t]); },
    tooltipStyle() {
      const px = (this.x(this.hover.year) / this.W) * 100;
      return { left: `min(max(${px}%, 110px), calc(100% - 190px))`, top: '-6px' };
    },
  },
  methods: {
    fmtM: formatM,
    fillFor(r) {
      if (r.operatingResult < 0) return 'var(--red)';
      if (r.year === 2027) return 'var(--red)';
      if (r.year > 2027) return `url(#${this.hatchId})`;
      return 'var(--ink)';
    },
  },
};
</script>

<style scoped>
.chart-box { position: relative; }
.chart { width: 100%; height: auto; display: block; }
.hatch-line { stroke: var(--muted); stroke-width: 2; }
.zero { stroke: var(--ink); stroke-width: 1.5; }
.tick { font-size: 11px; fill: var(--muted); font-variant-numeric: tabular-nums; }
.tick-strong { fill: var(--ink); font-weight: 700; }
.value { font-size: 11.5px; font-weight: 700; fill: var(--ink); font-variant-numeric: tabular-nums; }
.value-red { fill: var(--red); }
.tooltip {
  position: absolute; transform: translateX(-50%);
  background: var(--paper); border: 1px solid var(--ink); padding: 5px 10px;
  font-size: 12px; color: var(--ink); pointer-events: none; white-space: nowrap;
}
.tt-title { font-weight: 700; margin-right: 4px; }
</style>
